import React, { ReactNode, useState } from 'react'
import { Flex } from '@chakra-ui/react'
import { FormattedMessage } from 'react-intl'
import * as Yup from 'yup'
import { FormikValues } from 'formik'

import {
  Button,
  ErrorMessageBox,
  Heading,
  FieldSet,
  InfoMessageBox,
} from '@gaia-family/ds'

import { Fields, FormManager } from '@components/forms'
import { emailSchema } from '@components/forms/validation/commonSchemas'
import { Screen } from '@components/Layout'

import { useRequestPasswordResetMutation } from '@graphql'

const RequestPasswordErrors: Record<string, ReactNode> = {
  'The requested resource could not be found': (
    <FormattedMessage defaultMessage="Something went wrong, try again?" />
  ),
}

const RequestPasswordResetForm = () => {
  const [requestPasswordReset, { data }] = useRequestPasswordResetMutation()
  const [error, setError] = useState<ReactNode>()

  const validationSchema = Yup.object().shape({
    email: emailSchema(),
  })

  const handleSubmit = async ({ email }: FormikValues) => {
    setError(null)
    await requestPasswordReset({
      variables: {
        email,
      },
      onError: (error) => {
        setError(RequestPasswordErrors[error.message] || error.message)
      },
    })
  }

  return (
    <FormManager
      initialValues={{
        email: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <FieldSet>
          <Heading>
            <FormattedMessage defaultMessage="Request password reset" />
          </Heading>
          <Fields.Email
            name="email"
            label="Email"
            placeholder="Enter your email address"
          />
          {error && <ErrorMessageBox summary={error} />}
          {data?.requestPasswordReset && (
            <InfoMessageBox summary="If there's an account using this email address, we'll send a password reset link." />
          )}
          <Button
            mt={24}
            type="submit"
            disabled={isSubmitting || data?.requestPasswordReset === true}
          >
            {isSubmitting ? (
              <FormattedMessage defaultMessage="Sending request..." />
            ) : data?.requestPasswordReset ? (
              <FormattedMessage defaultMessage="Request sent" />
            ) : (
              <FormattedMessage defaultMessage="Request password reset" />
            )}
          </Button>
        </FieldSet>
      )}
    </FormManager>
  )
}

export const RequestPasswordReset = ({
  tokenError,
}: {
  tokenError: boolean
}) => {
  return (
    <Screen>
      <Flex flex={1} direction="column" width="full" maxWidth="prose">
        {tokenError ? (
          <ErrorMessageBox
            summary="Something went wrong"
            description="Please request a new password reset"
          />
        ) : null}
        <RequestPasswordResetForm />
      </Flex>
    </Screen>
  )
}
